import { useEffect, useMemo, useState } from 'react';
import { clientesApi, type ClienteResumo } from '@/features/clientes/api/clientesApi';
import { produtosApi, type ProdutoResumo } from '@/features/produtos/api/produtosApi';
import { servicosApi, type ServicoResumo } from '@/features/servicos/api/servicosApi';
import {
  orcamentosApi,
  type OrcamentoResumo,
} from '@/features/orcamentos/api/orcamentosApi';
import { ETAPA_CANCELADO } from '@/features/orcamentos/api/etapasKanban';
import { useEtapasKanban } from '@/shared/state/EtapasKanbanContext';
import {
  relatoriosApi,
  type UsoDetalhado,
  type HistoricoVeiculo,
} from '@/features/relatorios/api/relatoriosApi';
import { veiculosApi, type VeiculoResumo } from '@/features/veiculos/api/veiculosApi';
import {
  estoqueApi,
  type MovimentacaoEstoqueResumo,
} from '@/features/estoque/api/estoqueApi';
import { Button } from '@/shared/ui/Button';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Field } from '@/shared/ui/Field';
import { SearchInput, normalizarBusca } from '@/shared/ui/SearchInput';
import { IconRefresh } from '@/shared/ui/Icon';
import { BotaoOcultarValores } from '@/shared/ui/BotaoOcultarValores';
import { useOcultarValores } from '@/shared/state/OcultarValoresContext';
import { exportarCsv, type Tabela } from '@/shared/format/exportar';
import { imprimirComClasse, salvarComoPdf } from '@/shared/print/imprimir';
import { PrintPortal } from '@/shared/print/PrintPortal';
import { Modal } from '@/shared/ui/Modal';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { plataforma } from '@/platform';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  RelatorioImpressaoA4,
  aplicarPaginaA4,
  type Orientacao,
} from '@/features/relatorios/components/RelatorioImpressaoA4';
import { RelatorioAvaliacoes } from '@/features/relatorios/components/RelatorioAvaliacoes';
import { montarTabelasAvaliacoes } from '@/features/relatorios/components/avaliacoesRelatorio';
import { useRelatorioAvaliacoes } from '@/features/relatorios/hooks/useRelatorioAvaliacoes';
import { RelatorioClientes } from '@/features/relatorios/components/RelatorioClientes';
import { montarTabelasClientes } from '@/features/relatorios/components/clientesRelatorio';
import { useRelatorioClientes } from '@/features/relatorios/hooks/useRelatorioClientes';
import { FiltrosPeriodo } from '@/features/relatorios/components/FiltrosPeriodo';
import { isoDataLocal } from '@/shared/format/datas';
import {
  colaboradoresDosUsos,
  dataBaseOrcamento,
  filtrarMovimentacoes,
  filtrarOrcamentos,
  filtrarProdutosEstoque,
  filtrarUsos,
  itensDisponiveis,
  noPeriodo,
  rankingUsos,
  resumirColaboradores,
  resumoVisaoGeral,
  responsaveisDisponiveis,
  situacaoSaldo,
  cadastroPassa,
  estaAtrasada,
  ROTULO_ORIGEM,
  ROTULO_SITUACAO_ESTOQUE,
} from '@/features/relatorios/filtrosRelatorio';
import {
  chipsDeFiltros,
  filtrosAtivosColaboradores,
  filtrosAtivosEstoque,
  filtrosAtivosItens,
  filtrosAtivosOrcamentos,
  filtrosAtivosVeiculo,
  filtrosAtivosVisao,
  formatarDiaIso,
  rotuloFiltrosAtivos,
  PADRAO_COLABORADORES,
  PADRAO_ESTOQUE,
  PADRAO_ITENS,
  PADRAO_ORCAMENTOS,
  PADRAO_VEICULO,
  PADRAO_VISAO,
  type FiltroAtivo,
  type FiltrosAbaItens,
} from '@/features/relatorios/filtrosAtivos';
import { useFiltrosAba } from '@/features/relatorios/hooks/useFiltrosAba';
import { ChipsFiltros } from '@/features/relatorios/components/ChipsFiltros';
import {
  CheckboxFiltro,
  GradeFiltrosAba,
  SelectBaseData,
} from '@/features/relatorios/components/FiltrosAba';
import {
  TabelaColaboradores,
  TabelaEstoque,
  TabelaOrdensServico,
  TabelaUsos,
} from '@/features/relatorios/components/TabelasRelatorio';

const fmtMoedaReal = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

// Mesmo padrão de FinanceiroPage: bandeira de privacidade module-level
// que subcomponentes deste arquivo leem via `fmtMoeda.format` sem ter
// que threadar `ocultar` por toda a árvore. `RelatoriosPage` é o único
// componente exportado daqui — ele sincroniza com o contexto.
let __relatoriosOcultar = false;
const fmtMoeda = {
  format(v: number): string {
    return __relatoriosOcultar ? 'R$ ●●●●' : fmtMoedaReal.format(v || 0);
  },
};
const fmtData = new Intl.DateTimeFormat('pt-BR');
// Dia local — `toISOString()` usaria UTC e "Hoje" viraria amanhã após 21h.
const isoData = isoDataLocal;
const formatarQuantidade = (valor: number) => Math.trunc(valor).toLocaleString('pt-BR');

type Aba =
  | 'visao'
  | 'orcamentos'
  | 'clientes'
  | 'veiculo'
  | 'pecas'
  | 'servicos'
  | 'colaboradores'
  | 'estoque'
  | 'avaliacoes';

const ABAS: Array<{ id: Aba; label: string }> = [
  { id: 'visao', label: 'Visão geral' },
  { id: 'orcamentos', label: 'Ordens de serviço' },
  { id: 'clientes', label: 'Clientes' },
  { id: 'veiculo', label: 'Veículo' },
  { id: 'pecas', label: 'Peças' },
  { id: 'servicos', label: 'Serviços' },
  { id: 'colaboradores', label: 'Colaboradores' },
  { id: 'estoque', label: 'Estoque' },
  { id: 'avaliacoes', label: 'Avaliações' },
];

/** Abas que não usam o filtro de cliente (só o período). */
const ABAS_SEM_CLIENTE: ReadonlySet<Aba> = new Set<Aba>(['estoque', 'avaliacoes']);

export function RelatoriosPage() {
  const { ocultar } = useOcultarValores();
  __relatoriosOcultar = ocultar;
  const { etapas, rotuloDe } = useEtapasKanban();
  const hoje = useMemo(() => new Date(), []);
  const inicioMes = useMemo(() => {
    const d = new Date(hoje);
    d.setDate(1);
    return d;
  }, [hoje]);

  const [aba, setAba] = useState<Aba>('visao');
  const [dataInicio, setDataInicio] = useState(isoData(inicioMes));
  const [dataFim, setDataFim] = useState(isoData(hoje));
  const [clienteFiltro, setClienteFiltro] = useState('');

  // Filtros próprios de cada aba: cada uma guarda o seu (trocar de aba não
  // zera nada; "Limpar tudo" volta ao padrão da aba).
  const [fVisao, aplicarVisao] = useFiltrosAba('visao', PADRAO_VISAO);
  const [fOrcamentos, aplicarOrcamentos] = useFiltrosAba(
    'orcamentos',
    PADRAO_ORCAMENTOS,
  );
  const [fPecas, aplicarPecas] = useFiltrosAba('pecas', PADRAO_ITENS);
  const [fServicos, aplicarServicos] = useFiltrosAba('servicos', PADRAO_ITENS);
  const [fColaboradores, aplicarColaboradores] = useFiltrosAba(
    'colaboradores',
    PADRAO_COLABORADORES,
  );
  const [fEstoque, aplicarEstoque] = useFiltrosAba('estoque', PADRAO_ESTOQUE);
  const [fVeiculo, aplicarVeiculo] = useFiltrosAba('veiculo', PADRAO_VEICULO);

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const dialogos = useDialogos();
  const { estado } = useAuth();
  const nomeEmpresa = estado?.conta?.nomeEmpresa ?? 'Oficina Ágil';
  const [preVisualizando, setPreVisualizando] = useState(false);
  const [orientacao, setOrientacao] = useState<Orientacao>('retrato');
  const [emitidoEm, setEmitidoEm] = useState(() => new Date());

  const [clientes, setClientes] = useState<ClienteResumo[]>([]);
  const [produtos, setProdutos] = useState<ProdutoResumo[]>([]);
  const [servicos, setServicos] = useState<ServicoResumo[]>([]);
  const [orcamentos, setOrcamentos] = useState<OrcamentoResumo[]>([]);
  const [usos, setUsos] = useState<UsoDetalhado[]>([]);
  const [movimentacoesEstoque, setMovimentacoesEstoque] = useState<MovimentacaoEstoqueResumo[]>([]);

  const [veiculosLista, setVeiculosLista] = useState<VeiculoResumo[]>([]);
  const [veiculoSelecionadoId, setVeiculoSelecionadoId] = useState('');
  const [historicoVeiculo, setHistoricoVeiculo] = useState<HistoricoVeiculo | null>(null);
  const [carregandoVeiculo, setCarregandoVeiculo] = useState(false);

  // Avaliações moram no servidor (pós-venda): só busca com a aba aberta.
  const avaliacoes = useRelatorioAvaliacoes({
    inicio: dataInicio,
    fim: dataFim,
    habilitado: aba === 'avaliacoes',
  });

  // Relatório de clientes (gasto por cliente/veículo): só busca com a aba aberta.
  const relClientes = useRelatorioClientes({
    inicio: dataInicio,
    fim: dataFim,
    clienteId: clienteFiltro || undefined,
    habilitado: aba === 'clientes',
  });

  /**
   * Os itens (peças/serviços/colaboradores) vêm do backend já recortados
   * pelo período. Só a aba Colaboradores oferece data base de fechamento;
   * nas demais o recorte é sempre pela abertura.
   */
  const baseDataUsos = aba === 'colaboradores' ? fColaboradores.baseData : 'abertura';

  function recarregar() {
    if (aba === 'avaliacoes') avaliacoes.recarregar();
    if (aba === 'clientes') relClientes.recarregar();
    setCarregando(true);
    setErro(null);
    Promise.all([
      clientesApi.listar(),
      produtosApi.listar(),
      servicosApi.listar(),
      orcamentosApi.listar(),
      relatoriosApi.itensDetalhados({
        inicio: dataInicio,
        fim: dataFim,
        clienteId: clienteFiltro || undefined,
        baseData: baseDataUsos,
      }),
      estoqueApi.listarMovimentacoes(),
    ])
      .then(([cs, ps, ss, os, u, me]) => {
        setClientes(cs);
        setProdutos(ps);
        setServicos(ss);
        setOrcamentos(os);
        setUsos(u);
        setMovimentacoesEstoque(me);
      })
      .catch((e: unknown) => setErro(e instanceof Error ? e.message : 'Erro ao carregar dados.'))
      .finally(() => setCarregando(false));
  }

  useEffect(() => {
    recarregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInicio, dataFim, clienteFiltro, baseDataUsos]);

  // Lista de veículos carregada sob demanda na primeira vez que a aba abre
  // (não entra no `recarregar` para não custar uma chamada a cada mudança de
  // período/cliente nas outras abas).
  useEffect(() => {
    if (aba !== 'veiculo' || veiculosLista.length > 0) return;
    veiculosApi
      .listarTodos()
      .then(setVeiculosLista)
      .catch((e: unknown) =>
        setErro(e instanceof Error ? e.message : 'Erro ao carregar veículos.'),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aba]);

  // O histórico do veículo é independente do período: busca tudo do veículo
  // selecionado.
  useEffect(() => {
    if (!veiculoSelecionadoId) {
      setHistoricoVeiculo(null);
      return;
    }
    setCarregandoVeiculo(true);
    setErro(null);
    relatoriosApi
      .historicoVeiculo(veiculoSelecionadoId)
      .then(setHistoricoVeiculo)
      .catch((e: unknown) =>
        setErro(e instanceof Error ? e.message : 'Erro ao carregar histórico do veículo.'),
      )
      .finally(() => setCarregandoVeiculo(false));
  }, [veiculoSelecionadoId]);

  const orcamentosDoCliente = useMemo(
    () =>
      clienteFiltro === ''
        ? orcamentos
        : orcamentos.filter((o) => o.clienteId === clienteFiltro),
    [orcamentos, clienteFiltro],
  );

  /** O.S. do período pela data base escolhida (abertura ou fechamento). */
  function noPeriodoPelaBase(base: 'abertura' | 'fechamento'): OrcamentoResumo[] {
    return orcamentosDoCliente.filter((o) =>
      noPeriodo(dataBaseOrcamento(o, base), dataInicio, dataFim),
    );
  }

  // Visão geral
  const orcamentosNoPeriodo = useMemo(
    () => noPeriodoPelaBase(fVisao.baseData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orcamentosDoCliente, dataInicio, dataFim, fVisao.baseData],
  );

  const resumoVisao = useMemo(() => resumoVisaoGeral(orcamentosNoPeriodo), [orcamentosNoPeriodo]);

  // Uma linha por etapa configurada, na ordem do kanban. Status que não
  // existem mais na configuração entram no fim, para não sumir do relatório.
  const porStatus = useMemo(() => {
    const mapa: Record<string, { qtd: number; valor: number }> = {};
    for (const e of etapas) mapa[e.id] = { qtd: 0, valor: 0 };
    for (const o of orcamentosNoPeriodo) {
      const linha = (mapa[o.status] ??= { qtd: 0, valor: 0 });
      linha.qtd += 1;
      linha.valor += o.total;
    }
    return mapa;
  }, [orcamentosNoPeriodo, etapas]);

  /** Ids das linhas da tabela "por status", já na ordem certa. */
  const statusDoRelatorio = useMemo(() => Object.keys(porStatus), [porStatus]);

  // Ordens de serviço
  const orcamentosNoPeriodoOS = useMemo(
    () => noPeriodoPelaBase(fOrcamentos.baseData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orcamentosDoCliente, dataInicio, dataFim, fOrcamentos.baseData],
  );

  const orcamentosFiltrados = useMemo(
    () => filtrarOrcamentos(orcamentosNoPeriodoOS, fOrcamentos, nomeCliente),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orcamentosNoPeriodoOS, fOrcamentos, clientes],
  );

  const totaisOrcamentosFiltrados = useMemo(() => ({
    itens: orcamentosFiltrados.reduce((acc, o) => acc + o.quantidadeItens, 0),
    total: orcamentosFiltrados.reduce((acc, o) => acc + o.total, 0),
  }), [orcamentosFiltrados]);

  const responsaveis = useMemo(() => responsaveisDisponiveis(orcamentos), [orcamentos]);

  // Peças / Serviços
  const usosPecas = useMemo(() => usos.filter((u) => u.tipo === 'Produto'), [usos]);
  const usosServicos = useMemo(() => usos.filter((u) => u.tipo === 'Servico'), [usos]);

  const pecasFiltradas = useMemo(
    () => filtrarUsos(usosPecas, { ...fPecas, tipo: '' }),
    [usosPecas, fPecas],
  );
  const servicosFiltrados = useMemo(
    () => filtrarUsos(usosServicos, { ...fServicos, tipo: '' }),
    [usosServicos, fServicos],
  );

  /** Itens do select: só os que existem no status escolhido (+ o selecionado). */
  function itensDoSelect(lista: UsoDetalhado[], f: FiltrosAbaItens) {
    const noStatus = filtrarUsos(lista, {
      ...PADRAO_ITENS,
      status: f.status,
      tipo: '',
    });
    const itens = itensDisponiveis(noStatus);
    if (f.referenciaId && !itens.some((i) => i.id === f.referenciaId)) {
      const sel = lista.find((u) => u.referenciaId === f.referenciaId);
      if (sel) itens.unshift({ id: sel.referenciaId, descricao: sel.descricao });
    }
    return itens;
  }

  const pecasDisponiveis = useMemo(
    () => itensDoSelect(usosPecas, fPecas),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usosPecas, fPecas.status, fPecas.referenciaId],
  );
  const servicosDisponiveis = useMemo(
    () => itensDoSelect(usosServicos, fServicos),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usosServicos, fServicos.status, fServicos.referenciaId],
  );

  /** Todos os colaboradores que aparecem nos itens do período. */
  const colaboradoresDisponiveis = useMemo(() => colaboradoresDosUsos(usos), [usos]);

  // Colaboradores
  const usosComColaborador = useMemo(
    () => usos.filter((u) => u.colaboradorId && u.colaboradorNome),
    [usos],
  );

  const usosColaboradoresFiltrados = useMemo(
    () =>
      filtrarUsos(usosComColaborador, {
        ...PADRAO_ITENS,
        status: fColaboradores.status,
        colaborador: fColaboradores.colaborador,
        tipo: fColaboradores.tipo,
        busca: fColaboradores.busca,
      }),
    [usosComColaborador, fColaboradores],
  );

  const resumoColaboradores = useMemo(
    () => resumirColaboradores(usosColaboradoresFiltrados),
    [usosColaboradoresFiltrados],
  );

  // Estoque
  const movimentacoesEstoqueNoPeriodo = useMemo(
    () =>
      filtrarMovimentacoes(movimentacoesEstoque, {
        inicio: dataInicio,
        fim: dataFim,
        tipo: fEstoque.tipoMovimento,
        origem: fEstoque.origem,
        ocultarEstornadas: fEstoque.ocultarEstornadas,
        busca: fEstoque.busca,
      }),
    [movimentacoesEstoque, dataInicio, dataFim, fEstoque],
  );

  const produtosEstoqueFiltrados = useMemo(
    () => filtrarProdutosEstoque(produtos, fEstoque),
    [produtos, fEstoque],
  );

  const totaisEstoque = useMemo(() => {
    // Os cards contam o cadastro escolhido (padrão: só ativos).
    const doCadastro = produtos.filter((p) => cadastroPassa(fEstoque.cadastro, p.ativo));
    const saldo = (p: ProdutoResumo) => p.estoqueQuantidade ?? 0;
    const entradas = movimentacoesEstoqueNoPeriodo
      .filter((m) => m.tipo === 'Entrada')
      .reduce((acc, m) => acc + m.quantidade, 0);
    const saidas = movimentacoesEstoqueNoPeriodo
      .filter((m) => m.tipo === 'Saida')
      .reduce((acc, m) => acc + m.quantidade, 0);
    return {
      saldoAtual: doCadastro.reduce((acc, p) => acc + Math.max(saldo(p), 0), 0),
      produtosComEstoque: doCadastro.filter((p) => saldo(p) > 0).length,
      produtosSemEstoque: doCadastro.filter((p) => saldo(p) <= 0).length,
      produtosSaldoNegativo: doCadastro.filter((p) => saldo(p) < 0).length,
      entradas,
      saidas,
    };
  }, [produtos, fEstoque.cadastro, movimentacoesEstoqueNoPeriodo]);

  // Veículo: prontuário completo, com opção de esconder as canceladas.
  const historicoVeiculoVisivel = useMemo<HistoricoVeiculo | null>(() => {
    if (!historicoVeiculo || !fVeiculo.ocultarCanceladas) return historicoVeiculo;
    const visiveis = historicoVeiculo.orcamentos.filter((o) => o.status !== ETAPA_CANCELADO);
    return {
      ...historicoVeiculo,
      orcamentos: visiveis,
      resumo: { ...historicoVeiculo.resumo, totalOrcamentos: visiveis.length },
    };
  }, [historicoVeiculo, fVeiculo.ocultarCanceladas]);

  /**
   * Filtros ativos da aba atual: a mesma lista vira os chips (com ✕) e o
   * rótulo do relatório na tela, no CSV e no PDF.
   */
  function montarFiltrosAtivos(): FiltroAtivo[] {
    if (aba === 'veiculo') return filtrosAtivosVeiculo(fVeiculo, aplicarVeiculo);

    const periodoPadrao = { inicio: isoData(inicioMes), fim: isoData(hoje) };
    const periodoEhPadrao = dataInicio === periodoPadrao.inicio && dataFim === periodoPadrao.fim;
    const lista: FiltroAtivo[] = [
      {
        id: 'periodo',
        rotulo: 'Período',
        valor: `${formatarDiaIso(dataInicio)} a ${formatarDiaIso(dataFim)}`,
        limpar: () => {
          setDataInicio(periodoPadrao.inicio);
          setDataFim(periodoPadrao.fim);
        },
        ...(periodoEhPadrao ? { padrao: true } : {}),
      },
    ];
    if (!ABAS_SEM_CLIENTE.has(aba)) {
      lista.push({
        id: 'cliente',
        rotulo: 'Cliente',
        valor: clienteFiltro
          ? clientes.find((c) => c.id === clienteFiltro)?.nome ?? clienteFiltro
          : 'Todos os clientes',
        limpar: () => setClienteFiltro(''),
        ...(clienteFiltro ? {} : { padrao: true }),
      });
    }

    const ctx = {
      rotuloDe,
      nomeColaborador: (id: string) =>
        colaboradoresDisponiveis.find((c) => c.id === id)?.nome ??
        responsaveis.find((r) => r.id === id)?.nome ??
        id,
    };
    if (aba === 'visao') lista.push(...filtrosAtivosVisao(fVisao, aplicarVisao));
    else if (aba === 'orcamentos') {
      lista.push(...filtrosAtivosOrcamentos(fOrcamentos, aplicarOrcamentos, ctx));
    } else if (aba === 'pecas') {
      lista.push(
        ...filtrosAtivosItens(fPecas, aplicarPecas, {
          ...ctx,
          rotuloItem: 'Peça',
          nomeItem: (id) => usosPecas.find((u) => u.referenciaId === id)?.descricao ?? id,
        }),
      );
    } else if (aba === 'servicos') {
      lista.push(
        ...filtrosAtivosItens(fServicos, aplicarServicos, {
          ...ctx,
          rotuloItem: 'Serviço',
          nomeItem: (id) => usosServicos.find((u) => u.referenciaId === id)?.descricao ?? id,
        }),
      );
    } else if (aba === 'colaboradores') {
      lista.push(...filtrosAtivosColaboradores(fColaboradores, aplicarColaboradores, ctx));
    } else if (aba === 'estoque') {
      lista.push(...filtrosAtivosEstoque(fEstoque, aplicarEstoque));
    }
    return lista;
  }

  const filtrosAtivos = montarFiltrosAtivos();
  const chipsFiltros = chipsDeFiltros(filtrosAtivos);

  function limparTodosFiltros() {
    for (const f of chipsFiltros) f.limpar();
  }

  function rotuloFiltros(): string {
    return rotuloFiltrosAtivos(filtrosAtivos);
  }

  function nomeCliente(id: string): string {
    return clientes.find((c) => c.id === id)?.nome ?? '(removido)';
  }

  /**
   * Monta o conteúdo do relatório da aba atual — só os dados, sem nada de
   * UI. É a mesma fonte do CSV e da folha A4: o que o usuário vê na prévia
   * é exatamente o que sai no Excel.
   */
  function montarTabelas(): Tabela[] {
    let tabelas: Tabela[];
    if (aba === 'visao') {
      const r = resumoVisao;
      tabelas = [
        {
          titulo: `Visão geral — ${rotuloFiltros()}`,
          cabecalho: ['Indicador', 'Quantidade', 'Valor'],
          colunasNumero: [1],
          colunasMoeda: [2],
          linhas: [
            ['Ordens de serviço no período', r.total, ''],
            ['Fechadas (faturado)', r.fechadas, r.faturado.toFixed(2)],
            ['Em aberto', r.emAberto, r.valorEmAberto.toFixed(2)],
            ['Canceladas', r.canceladas, r.valorCanceladas.toFixed(2)],
            ['Ticket médio', '', r.ticketMedio.toFixed(2)],
          ],
        },
        {
          titulo: 'Ordens de serviço por status',
          cabecalho: ['Status', 'Quantidade', 'Valor total'],
          colunasNumero: [1],
          colunasMoeda: [2],
          linhas: statusDoRelatorio.map((s) => [
            rotuloDe(s),
            porStatus[s].qtd,
            porStatus[s].valor.toFixed(2),
          ]),
          rodape: [
            'Totais',
            statusDoRelatorio.reduce((acc, s) => acc + porStatus[s].qtd, 0),
            statusDoRelatorio.reduce((acc, s) => acc + porStatus[s].valor, 0).toFixed(2),
          ],
        },
      ];
    } else if (aba === 'orcamentos') {
      tabelas = [
        {
          titulo: `Ordens de serviço — ${rotuloFiltros()}`,
          cabecalho: [
            'Número',
            'Data',
            'Cliente',
            'Veículo',
            'Responsável',
            'KM',
            'Status',
            'Itens',
            'Total',
          ],
          colunasNumero: [5, 7],
          colunasMoeda: [8],
          linhas: orcamentosFiltrados.map((o) => [
            o.numero,
            fmtData.format(new Date(o.data)),
            nomeCliente(o.clienteId),
            o.veiculoDescricao,
            o.colaboradorResponsavelNome ?? '—',
            o.kmRegistrado != null ? o.kmRegistrado.toLocaleString('pt-BR') : '—',
            estaAtrasada(o) ? `${rotuloDe(o.status)} (atrasada)` : rotuloDe(o.status),
            o.quantidadeItens,
            o.total.toFixed(2),
          ]),
          rodape: [
            'Totais',
            '',
            '',
            '',
            '',
            '',
            '',
            totaisOrcamentosFiltrados.itens,
            totaisOrcamentosFiltrados.total.toFixed(2),
          ],
        },
      ];
    } else if (aba === 'veiculo') {
      if (!historicoVeiculoVisivel) {
        tabelas = [
          {
            titulo: 'Veículo',
            cabecalho: ['Aviso'],
            linhas: [['Selecione um veículo para exportar o histórico.']],
          },
        ];
      } else {
        const v = historicoVeiculoVisivel.veiculo;
        const fmtDataIso = (iso: string) => fmtData.format(new Date(iso));
        tabelas = [
          {
            titulo: `Veículo ${v.descricao} — ${v.marca} ${v.modelo}`.trim(),
            cabecalho: ['Campo', 'Valor'],
            linhas: [
              ['Placa', v.placa ?? 'Sem placa'],
              ['Marca/Modelo', `${v.marca} ${v.modelo}`.trim()],
              ['Ano', v.ano ?? '—'],
              ['Motor', v.motor ?? '—'],
              ['KM atual', v.kmAtual != null ? formatarQuantidade(v.kmAtual) : '—'],
              ['Cliente atual', v.clienteAtualNome ?? '—'],
              ['Ordens de serviço', historicoVeiculoVisivel.resumo.totalOrcamentos],
              ['Total fechado/pago', historicoVeiculoVisivel.resumo.totalFechado.toFixed(2)],
            ],
          },
          {
            titulo: 'Histórico de KM',
            cabecalho: ['Data', 'KM', 'Origem', 'Observação'],
            colunasNumero: [1],
            linhas: historicoVeiculoVisivel.kmHistorico.map((h) => [
              fmtDataIso(h.data),
              formatarQuantidade(h.km),
              h.origem,
              h.observacao ?? '—',
            ]),
          },
          {
            titulo: 'Ordens de serviço e itens',
            cabecalho: [
              'OS',
              'Data',
              'Status',
              'Cliente',
              'Tipo',
              'Item',
              'Colaborador',
              'Qtd.',
              'Preço unit.',
              'Desconto',
              'Subtotal',
            ],
            colunasNumero: [7],
            colunasMoeda: [8, 9, 10],
            rodape: [
              'Totais',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              '',
              historicoVeiculoVisivel.orcamentos
                .reduce((acc, o) => acc + o.total, 0)
                .toFixed(2),
            ],
            linhas: historicoVeiculoVisivel.orcamentos.flatMap((o) =>
              o.itens.length === 0
                ? [[
                    o.numero,
                    fmtDataIso(o.data),
                    rotuloDe(o.status),
                    o.clienteNome,
                    '—',
                    '(sem itens)',
                    '',
                    '',
                    '',
                    '',
                    o.total.toFixed(2),
                  ]]
                : o.itens.map((it) => [
                    o.numero,
                    fmtDataIso(o.data),
                    rotuloDe(o.status),
                    o.clienteNome,
                    it.tipo === 'Produto' ? 'Peça' : 'Serviço',
                    it.descricao,
                    it.colaboradorNome ?? '—',
                    formatarQuantidade(it.quantidade),
                    it.precoUnitario.toFixed(2),
                    it.valorDesconto.toFixed(2),
                    it.subtotal.toFixed(2),
                  ]),
            ),
          },
        ];
      }
    } else if (aba === 'pecas') {
      tabelas = tabelasItens('Peças', 'Peça', pecasFiltradas, fPecas);
    } else if (aba === 'servicos') {
      tabelas = tabelasItens('Serviços', 'Serviço', servicosFiltrados, fServicos);
    } else if (aba === 'colaboradores') {
      tabelas = [
        {
          titulo: `Colaboradores — resumo — ${rotuloFiltros()}`,
          cabecalho: ['Colaborador', 'Peças', 'Serviços', 'Qtd. total', 'Valor total'],
          colunasNumero: [1, 2, 3],
          colunasMoeda: [4],
          rodape: [
            'Totais',
            resumoColaboradores.reduce((acc, r) => acc + r.pecas, 0),
            resumoColaboradores.reduce((acc, r) => acc + r.servicos, 0),
            resumoColaboradores.reduce((acc, r) => acc + r.quantidade, 0),
            resumoColaboradores.reduce((acc, r) => acc + r.total, 0).toFixed(2),
          ],
          linhas: resumoColaboradores.map((r) => [
            r.colaboradorNome,
            r.pecas,
            r.servicos,
            r.quantidade,
            r.total.toFixed(2),
          ]),
        },
        {
          titulo: `Colaboradores — itens — ${rotuloFiltros()}`,
          cabecalho: ['Colaborador', 'Tipo', 'Item', 'OS', 'Data', 'Cliente', 'Qtd.', 'Subtotal'],
          colunasNumero: [6],
          colunasMoeda: [7],
          linhas: usosColaboradoresFiltrados.map((u) => [
            u.colaboradorNome ?? '',
            u.tipo === 'Produto' ? 'Peça' : 'Serviço',
            u.descricao,
            u.orcamentoNumero,
            fmtData.format(new Date(u.orcamentoData)),
            u.clienteNome,
            u.quantidade,
            u.subtotal.toFixed(2),
          ]),
        },
      ];
    } else if (aba === 'clientes') {
      tabelas = montarTabelasClientes(relClientes.dados, {
        rotuloPeriodo: rotuloFiltros(),
        ordenacao: relClientes.ordenacao,
        filtros: [
          relClientes.somenteFechados ? 'Só O.S. fechadas' : '',
          relClientes.incluirSemCompra ? 'Inclui clientes sem O.S.' : '',
          relClientes.buscaAplicada ? `Busca: "${relClientes.buscaAplicada}"` : '',
        ].filter(Boolean),
      });
    } else if (aba === 'avaliacoes') {
      const nomeResponsavel = avaliacoes.filtros.responsavelId
        ? avaliacoes.responsaveis.find((r) => r.id === avaliacoes.filtros.responsavelId)?.nome
        : null;
      tabelas = montarTabelasAvaliacoes(avaliacoes.dados, {
        rotuloPeriodo: rotuloFiltros(),
        filtros: avaliacoes.filtros,
        nomeResponsavel,
      });
    } else {
      tabelas = [
        {
          titulo: `Estoque — saldo atual — ${rotuloFiltros()}`,
          cabecalho: ['Código', 'Produto', 'Estoque atual', 'Situação', 'Cadastro'],
          colunasNumero: [2],
          rodape: [
            'Totais',
            `${produtosEstoqueFiltrados.length} produto(s)`,
            // Soma só o que existe na prateleira (negativo não "desconta").
            formatarQuantidade(
              produtosEstoqueFiltrados.reduce(
                (acc, p) => acc + Math.max(p.estoqueQuantidade ?? 0, 0),
                0,
              ),
            ),
            '',
            '',
          ],
          linhas: produtosEstoqueFiltrados.map((p) => [
            p.codigo,
            p.nome,
            // Saldo real: o negativo aparece como negativo.
            formatarQuantidade(p.estoqueQuantidade ?? 0),
            ROTULO_SITUACAO_ESTOQUE[situacaoSaldo(p.estoqueQuantidade ?? 0)],
            p.ativo ? 'Ativo' : 'Inativo',
          ]),
        },
        {
          titulo: `Movimentações de estoque — ${rotuloFiltros()}`,
          cabecalho: [
            'Data',
            'Produto',
            'Tipo',
            'Origem',
            'Qtd.',
            'Saldo antes',
            'Saldo depois',
            'Motivo',
          ],
          colunasNumero: [4, 5, 6],
          rodape: [
            'Totais',
            '',
            `${formatarQuantidade(totaisEstoque.entradas)} entrada(s) / ${formatarQuantidade(totaisEstoque.saidas)} saída(s)`,
            '',
            formatarQuantidade(
              movimentacoesEstoqueNoPeriodo.reduce((acc, m) => acc + m.quantidade, 0),
            ),
            '',
            '',
            '',
          ],
          linhas: movimentacoesEstoqueNoPeriodo.map((m) => [
            fmtData.format(new Date(m.criadoEm)),
            `${m.produtoCodigo} - ${m.produtoNome}`,
            m.tipo === 'Entrada' ? 'Entrada' : 'Saída',
            `${ROTULO_ORIGEM[m.origemTipo] ?? m.origemTipo}${m.estornada ? ' (estornada)' : ''}`,
            formatarQuantidade(m.quantidade),
            formatarQuantidade(m.saldoAntes),
            formatarQuantidade(m.saldoDepois),
            m.motivo,
          ]),
        },
      ];
    }
    return tabelas;
  }

  /** Peças/Serviços: detalhado (uma linha por item de O.S.) ou ranking. */
  function tabelasItens(
    titulo: string,
    rotuloItem: string,
    linhas: UsoDetalhado[],
    f: FiltrosAbaItens,
  ): Tabela[] {
    if (f.visao === 'ranking') {
      const ranking = rankingUsos(linhas);
      return [
        {
          titulo: `${titulo} — ranking — ${rotuloFiltros()}`,
          cabecalho: ['#', rotuloItem, 'Qtd. total', 'Nº de O.S.', 'Desconto', 'Valor'],
          colunasNumero: [0, 2, 3],
          colunasMoeda: [4, 5],
          rodape: [
            'Totais',
            `${ranking.length} item(ns)`,
            ranking.reduce((acc, r) => acc + r.quantidade, 0),
            '',
            ranking.reduce((acc, r) => acc + r.desconto, 0).toFixed(2),
            ranking.reduce((acc, r) => acc + r.valor, 0).toFixed(2),
          ],
          linhas: ranking.map((r, i) => [
            i + 1,
            r.descricao,
            r.quantidade,
            r.ordens,
            r.desconto.toFixed(2),
            r.valor.toFixed(2),
          ]),
        },
      ];
    }
    return [
      {
        titulo: `${titulo} — ${rotuloFiltros()}`,
        cabecalho: [
          'OS',
          'Data',
          'Cliente',
          rotuloItem,
          'Colaborador',
          'Qtd.',
          'Preço unit.',
          'Desconto',
          'Subtotal',
        ],
        colunasNumero: [5],
        colunasMoeda: [6, 7, 8],
        // Preço unitário não se soma — a célula do rodapé fica vazia.
        rodape: [
          'Totais',
          '',
          '',
          '',
          '',
          linhas.reduce((acc, u) => acc + u.quantidade, 0),
          '',
          linhas.reduce((acc, u) => acc + u.valorDesconto, 0).toFixed(2),
          linhas.reduce((acc, u) => acc + u.subtotal, 0).toFixed(2),
        ],
        linhas: linhas.map((u) => [
          u.orcamentoNumero,
          fmtData.format(new Date(u.orcamentoData)),
          u.clienteNome,
          u.descricao,
          u.colaboradorNome ?? '—',
          u.quantidade,
          u.precoUnitario.toFixed(2),
          u.valorDesconto.toFixed(2),
          u.subtotal.toFixed(2),
        ]),
      },
    ];
  }

  function handleExportarCsv() {
    exportarCsv(`relatorio_${aba}_${dataInicio}_${dataFim}`, montarTabelas());
  }

  const tituloRelatorio = ABAS.find((a) => a.id === aba)?.label ?? 'Relatório';

  /** Contexto que aparece embaixo do cabeçalho da folha. */
  function subtituloImpressao(): string {
    if (aba === 'veiculo') {
      if (!historicoVeiculo) return 'Nenhum veículo selecionado.';
      const v = historicoVeiculo.veiculo;
      return fVeiculo.ocultarCanceladas
        ? `Veículo: ${v.descricao} • ${v.placa ?? 'sem placa'} • histórico sem as O.S. canceladas`
        : `Veículo: ${v.descricao} • ${v.placa ?? 'sem placa'} • histórico completo`;
    }
    return rotuloFiltros();
  }

  /**
   * Só monta as tabelas quando a prévia está aberta — fora dela o documento
   * nem existe, então nada disso pesa na navegação entre abas.
   */
  const tabelasImpressao = preVisualizando ? montarTabelas() : [];

  function abrirPrevia() {
    // Relatório com muitas colunas não cabe em retrato: já abre deitado,
    // e o usuário troca no rodapé da prévia se quiser.
    const largo = montarTabelas().some((t) => t.cabecalho.length > 7);
    setOrientacao(largo ? 'paisagem' : 'retrato');
    setEmitidoEm(new Date());
    setPreVisualizando(true);
  }

  const nomeArquivoPdf = `relatorio-${aba}-${dataInicio}_a_${dataFim}.pdf`;

  async function imprimirRelatorio() {
    const limparPagina = aplicarPaginaA4(orientacao);
    try {
      const r = await imprimirComClasse('imprimindo-relatorio');
      if (!r.ok && !r.cancelado) {
        await dialogos.alertar(r.erro ?? 'Falha ao imprimir.');
      }
    } finally {
      limparPagina();
    }
  }

  async function baixarPdfRelatorio() {
    const limparPagina = aplicarPaginaA4(orientacao);
    try {
      const r = await salvarComoPdf('imprimindo-relatorio', nomeArquivoPdf);
      if (!r.ok && !r.cancelado) {
        await dialogos.alertar(r.erro ?? 'Falha ao gerar PDF.');
      }
    } finally {
      limparPagina();
    }
  }

  return (
    <section className="page">
      <PageHeader
        titulo="Relatórios"
        descricao="Visão geral, ordens de serviço, gasto por cliente, peças, serviços, colaboradores, estoque e avaliações dos clientes. Cada aba é um relatório independente."
        acoes={
          <>
            <BotaoOcultarValores />
            <Button
              onClick={recarregar}
              disabled={carregando || (aba === 'avaliacoes' && avaliacoes.carregando)}
            >
              <IconRefresh className="btn__icon" />
              {carregando || (aba === 'avaliacoes' && avaliacoes.carregando)
                ? 'Atualizando…'
                : 'Atualizar'}
            </Button>
            <Button onClick={handleExportarCsv}>Exportar Excel</Button>
            <Button variante="primary" onClick={abrirPrevia}>
              Imprimir / PDF (A4)
            </Button>
          </>
        }
      />

      {erro && (
        <p role="alert" className="alert alert--error">
          Falha ao carregar: {erro}
        </p>
      )}

      {aba !== 'veiculo' && (
        <FiltrosPeriodo
          inicio={dataInicio}
          fim={dataFim}
          onPeriodo={(inicio, fim) => {
            setDataInicio(inicio);
            setDataFim(fim);
          }}
          quantidadeFiltros={chipsFiltros.filter((f) => f.id !== 'periodo').length}
        >
            {!ABAS_SEM_CLIENTE.has(aba) && (
              <Field rotulo="Cliente">
                {(id) => (
                  <select
                    id={id}
                    className="input"
                    value={clienteFiltro}
                    onChange={(e) => setClienteFiltro(e.target.value)}
                  >
                    <option value="">Todos os clientes</option>
                    {clientes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                        {c.documento ? ` (${c.documento})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
        </FiltrosPeriodo>
      )}

      <ChipsFiltros filtros={chipsFiltros} onLimparTudo={limparTodosFiltros} />

      <div className="tabs" role="tablist">
        {ABAS.map((a) => (
          <button
            key={a.id}
            role="tab"
            aria-selected={aba === a.id}
            className={`tabs__item${aba === a.id ? ' tabs__item--ativa' : ''}`}
            onClick={() => setAba(a.id)}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div id="area-relatorio">
        {aba !== 'veiculo' && (
          <p className="hint" style={{ marginBottom: 12 }}>{rotuloFiltros()}</p>
        )}

        {aba === 'visao' && (
          <>
            <GradeFiltrosAba>
              <SelectBaseData
                valor={fVisao.baseData}
                onChange={(baseData) => aplicarVisao({ baseData })}
              />
            </GradeFiltrosAba>
            <VisaoGeral
              resumo={resumoVisao}
              porStatus={porStatus}
              statusDoRelatorio={statusDoRelatorio}
              rotuloDe={rotuloDe}
              totalClientes={clientes.length}
              totalProdutos={produtos.length}
              totalServicos={servicos.length}
              totalOrcamentos={orcamentos.length}
            />
          </>
        )}

        {aba === 'orcamentos' && (
          <TabelaOrdensServico
            linhas={orcamentosFiltrados}
            totalNoPeriodo={orcamentosNoPeriodoOS.length}
            filtros={fOrcamentos}
            aplicar={aplicarOrcamentos}
            etapas={etapas}
            responsaveis={responsaveis}
            nomeCliente={nomeCliente}
            rotuloDe={rotuloDe}
          />
        )}

        {aba === 'veiculo' && (
          <RelatorioVeiculo
            veiculos={veiculosLista}
            veiculoSelecionadoId={veiculoSelecionadoId}
            onSelecionar={setVeiculoSelecionadoId}
            historico={historicoVeiculoVisivel}
            carregando={carregandoVeiculo}
            nomeCliente={nomeCliente}
            ocultarCanceladas={fVeiculo.ocultarCanceladas}
            onOcultarCanceladas={(ocultarCanceladas) => aplicarVeiculo({ ocultarCanceladas })}
          />
        )}

        {aba === 'pecas' && (
          <TabelaUsos
            titulo="Peças utilizadas"
            rotuloItem="Peça"
            rotuloTodos="Todas as peças"
            placeholder="Buscar por peça, ordem de serviço, cliente ou colaborador…"
            linhas={pecasFiltradas}
            totalSemFiltro={usosPecas.length}
            filtros={fPecas}
            aplicar={aplicarPecas}
            etapas={etapas}
            itens={pecasDisponiveis}
            colaboradores={colaboradoresDisponiveis}
          />
        )}

        {aba === 'servicos' && (
          <TabelaUsos
            titulo="Serviços utilizados"
            rotuloItem="Serviço"
            rotuloTodos="Todos os serviços"
            placeholder="Buscar por serviço, ordem de serviço, cliente ou colaborador…"
            linhas={servicosFiltrados}
            totalSemFiltro={usosServicos.length}
            filtros={fServicos}
            aplicar={aplicarServicos}
            etapas={etapas}
            itens={servicosDisponiveis}
            colaboradores={colaboradoresDisponiveis}
          />
        )}

        {aba === 'colaboradores' && (
          <TabelaColaboradores
            linhas={usosColaboradoresFiltrados}
            resumo={resumoColaboradores}
            totalSemFiltro={usosComColaborador.length}
            filtros={fColaboradores}
            aplicar={aplicarColaboradores}
            etapas={etapas}
            colaboradoresDisponiveis={colaboradoresDisponiveis}
          />
        )}

        {aba === 'estoque' && (
          <TabelaEstoque
            produtos={produtosEstoqueFiltrados}
            totalProdutos={produtos.length}
            movimentacoes={movimentacoesEstoqueNoPeriodo}
            totais={totaisEstoque}
            filtros={fEstoque}
            aplicar={aplicarEstoque}
          />
        )}

        {aba === 'avaliacoes' && <RelatorioAvaliacoes estado={avaliacoes} />}

        {aba === 'clientes' && (
          <RelatorioClientes
            estado={relClientes}
            onAbrirVeiculo={(id) => {
              setVeiculoSelecionadoId(id);
              setAba('veiculo');
            }}
          />
        )}
      </div>

      {preVisualizando && (
        <>
          {/* Fora do #root: é isto que vai pro papel, não a tela. */}
          <PrintPortal>
            <RelatorioImpressaoA4
              nomeEmpresa={nomeEmpresa}
              titulo={tituloRelatorio}
              subtitulo={subtituloImpressao()}
              emitidoEm={emitidoEm}
              tabelas={tabelasImpressao}
            />
          </PrintPortal>

          <Modal
            aberto
            tamanho="lg"
            titulo={`Pré-visualização — ${tituloRelatorio} (A4)`}
            onFechar={() => setPreVisualizando(false)}
            rodape={
              <>
                <Button onClick={() => setPreVisualizando(false)}>Fechar</Button>
                <Button
                  onClick={() =>
                    setOrientacao((o) => (o === 'retrato' ? 'paisagem' : 'retrato'))
                  }
                >
                  {orientacao === 'retrato' ? 'Virar para paisagem' : 'Virar para retrato'}
                </Button>
                <Button onClick={() => void baixarPdfRelatorio()}>
                  {plataforma.capacidades.salvarPdfNativo ? 'Baixar PDF' : 'Salvar como PDF'}
                </Button>
                <Button variante="primary" onClick={() => void imprimirRelatorio()}>
                  Imprimir
                </Button>
              </>
            }
          >
            <p className="hint" style={{ marginBottom: 8 }}>
              Folha A4 {orientacao === 'paisagem' ? 'deitada' : 'em pé'} — o conteúdo é
              quebrado em páginas na hora de imprimir, repetindo o cabeçalho da tabela.
              {!plataforma.capacidades.salvarPdfNativo && (
                <>
                  {' '}
                  No diálogo do navegador, deixe <strong>Margens</strong> em "Padrão" e
                  marque <strong>Gráficos de segundo plano</strong> para o PDF sair igual
                  à prévia.
                </>
              )}
            </p>
            <div
              className={`preview-impressao preview-impressao--${
                orientacao === 'paisagem' ? 'a4-paisagem' : 'a4'
              }`}
            >
              <RelatorioImpressaoA4
                nomeEmpresa={nomeEmpresa}
                titulo={tituloRelatorio}
                subtitulo={subtituloImpressao()}
                emitidoEm={emitidoEm}
                tabelas={tabelasImpressao}
              />
            </div>
          </Modal>
        </>
      )}
    </section>
  );
}

function VisaoGeral(props: {
  resumo: ReturnType<typeof resumoVisaoGeral>;
  porStatus: Record<string, { qtd: number; valor: number }>;
  statusDoRelatorio: string[];
  rotuloDe: (id: string) => string;
  totalClientes: number;
  totalProdutos: number;
  totalServicos: number;
  totalOrcamentos: number;
}) {
  return (
    <>
      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-card__label">OS no período</span>
          <span className="stat-card__value">{props.resumo.total}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Faturado (fechados)</span>
          <span className="stat-card__value">{fmtMoeda.format(props.resumo.faturado)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Em aberto ({props.resumo.emAberto})</span>
          <span className="stat-card__value">{fmtMoeda.format(props.resumo.valorEmAberto)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Canceladas ({props.resumo.canceladas})</span>
          <span className="stat-card__value stat-card__value--danger">
            {fmtMoeda.format(props.resumo.valorCanceladas)}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Ticket médio</span>
          <span className="stat-card__value">{fmtMoeda.format(props.resumo.ticketMedio)}</span>
        </div>
      </div>

      <div className="stats-grid" style={{ marginTop: 16 }}>
        <div className="stat-card">
          <span className="stat-card__label">Total clientes</span>
          <span className="stat-card__value">{props.totalClientes}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Total produtos</span>
          <span className="stat-card__value">{props.totalProdutos}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Total serviços</span>
          <span className="stat-card__value">{props.totalServicos}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">OS (todas)</span>
          <span className="stat-card__value">{props.totalOrcamentos}</span>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card__header">
          <span className="card__title">Ordens de serviço por status</span>
        </div>
        <div className="card__body card__body--flush">
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th className="table__numeric">Quantidade</th>
                  <th className="table__numeric">Valor total</th>
                </tr>
              </thead>
              <tbody>
                {props.statusDoRelatorio.map((s) => (
                  <tr key={s}>
                    <td className="table__primary">{props.rotuloDe(s)}</td>
                    <td className="table__numeric">{props.porStatus[s].qtd}</td>
                    <td className="table__numeric">
                      {fmtMoeda.format(props.porStatus[s].valor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function RelatorioVeiculo({
  veiculos,
  veiculoSelecionadoId,
  onSelecionar,
  historico,
  carregando,
  nomeCliente,
  ocultarCanceladas,
  onOcultarCanceladas,
}: {
  veiculos: VeiculoResumo[];
  veiculoSelecionadoId: string;
  onSelecionar: (id: string) => void;
  historico: HistoricoVeiculo | null;
  carregando: boolean;
  nomeCliente: (id: string) => string;
  ocultarCanceladas: boolean;
  onOcultarCanceladas: (v: boolean) => void;
}) {
  const [busca, setBusca] = useState('');

  const veiculosOrdenados = useMemo(
    // Sem placa ordena pela descrição (marca/modelo/ano) — mistura os dois
    // tipos numa lista alfabética única em vez de deixar buracos.
    () => [...veiculos].sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR')),
    [veiculos],
  );

  const veiculosFiltrados = useMemo(() => {
    const q = normalizarBusca(busca.trim());
    if (!q) return veiculosOrdenados;
    return veiculosOrdenados.filter((v) =>
      normalizarBusca(`${v.descricao} ${v.marca} ${v.modelo} ${nomeCliente(v.clienteId)}`).includes(q),
    );
  }, [veiculosOrdenados, busca, nomeCliente]);

  return (
    <>
      <div className="card">
        <div className="card__header card__header--com-busca">
          <span className="card__title">Selecione o veículo</span>
          <div className="secao-tabela__filtros">
            <SearchInput
              valor={busca}
              onChange={setBusca}
              placeholder="Buscar por placa, modelo ou cliente…"
            />
            <select
              aria-label="Selecionar veículo"
              className="input"
              value={veiculoSelecionadoId}
              onChange={(e) => onSelecionar(e.target.value)}
            >
              <option value="">— Escolha um veículo —</option>
              {veiculosFiltrados.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.descricao} · {v.marca} {v.modelo} · {nomeCliente(v.clienteId)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="filtros-aba">
          <CheckboxFiltro
            rotulo="Ocultar O.S. canceladas"
            marcado={ocultarCanceladas}
            onChange={onOcultarCanceladas}
          />
        </div>
      </div>

      {!veiculoSelecionadoId ? (
        <p className="empty-state">
          Escolha um veículo acima para ver o histórico de KM, ordens de serviço e os serviços e peças
          aplicados.
        </p>
      ) : carregando ? (
        <p className="empty-state">Carregando histórico…</p>
      ) : !historico ? (
        <p className="empty-state">Nenhum dado encontrado para este veículo.</p>
      ) : (
        <FichaVeiculo historico={historico} />
      )}
    </>
  );
}

function FichaVeiculo({ historico }: { historico: HistoricoVeiculo }) {
  const { veiculo, kmHistorico, orcamentos, resumo } = historico;
  return (
    <>
      <div className="card">
        <div className="card__header">
          <span className="card__title">
            {veiculo.descricao} — {veiculo.marca} {veiculo.modelo}
          </span>
        </div>
        <div className="card__body">
          <div className="ficha-veiculo__dados">
            <span><strong>Placa:</strong> {veiculo.placa ?? 'Sem placa'}</span>
            <span><strong>Marca/Modelo:</strong> {veiculo.marca} {veiculo.modelo}</span>
            <span><strong>Ano:</strong> {veiculo.ano ?? '—'}</span>
            <span><strong>Motor:</strong> {veiculo.motor ?? '—'}</span>
            <span>
              <strong>KM atual:</strong>{' '}
              {veiculo.kmAtual != null ? `${formatarQuantidade(veiculo.kmAtual)} km` : '—'}
            </span>
            <span><strong>Cliente atual:</strong> {veiculo.clienteAtualNome ?? '—'}</span>
          </div>
        </div>
      </div>

      <div className="stats-grid" style={{ marginTop: 16 }}>
        <div className="stat-card">
          <span className="stat-card__label">OS</span>
          <span className="stat-card__value">{resumo.totalOrcamentos}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Total fechado/pago</span>
          <span className="stat-card__value">{fmtMoeda.format(resumo.totalFechado)}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Primeira visita</span>
          <span className="stat-card__value">
            {resumo.primeiraVisita ? fmtData.format(new Date(resumo.primeiraVisita)) : '—'}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Última visita</span>
          <span className="stat-card__value">
            {resumo.ultimaVisita ? fmtData.format(new Date(resumo.ultimaVisita)) : '—'}
          </span>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card__header">
          <span className="card__title">Histórico de quilometragem ({kmHistorico.length})</span>
        </div>
        <div className="card__body card__body--flush">
          {kmHistorico.length === 0 ? (
            <p className="empty-state">Nenhum registro de KM.</p>
          ) : (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th className="table__numeric">KM</th>
                    <th>Origem</th>
                    <th>Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {kmHistorico.map((h) => (
                    <tr key={h.id}>
                      <td className="table__muted">{fmtData.format(new Date(h.data))}</td>
                      <td className="table__numeric">{formatarQuantidade(h.km)}</td>
                      <td>
                        <span className="badge badge--neutro">{h.origem}</span>
                      </td>
                      <td>{h.observacao ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card__header">
          <span className="card__title">Ordens de serviço do veículo ({orcamentos.length})</span>
        </div>
        <div className="card__body">
          {orcamentos.length === 0 ? (
            <p className="empty-state">Nenhuma ordem de serviço para este veículo.</p>
          ) : (
            orcamentos.map((o) => <OrcamentoDetalhe key={o.id} orcamento={o} />)
          )}
        </div>
      </div>
    </>
  );
}

function OrcamentoDetalhe({
  orcamento: o,
}: {
  orcamento: HistoricoVeiculo['orcamentos'][number];
}) {
  const { rotuloDe } = useEtapasKanban();
  const pecas = o.itens.filter((it) => it.tipo === 'Produto');
  const servicos = o.itens.filter((it) => it.tipo === 'Servico');
  return (
    <div className="orcamento-detalhe">
      <div className="orcamento-detalhe__cabecalho">
        <div>
          <span className="table__primary">{o.numero}</span>
          <span className="table__muted"> · {fmtData.format(new Date(o.data))}</span>
        </div>
        <span className="badge">{rotuloDe(o.status)}</span>
        <span className="table__muted">Cliente: {o.clienteNome}</span>
        <span className="table__primary">{fmtMoeda.format(o.total)}</span>
      </div>

      {o.itens.length === 0 ? (
        <p className="hint">Sem itens.</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Item</th>
                <th>Colaborador</th>
                <th className="table__numeric">Qtd.</th>
                <th className="table__numeric">Preço unit.</th>
                <th className="table__numeric">Desconto</th>
                <th className="table__numeric">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {[...servicos, ...pecas].map((it, idx) => (
                <tr key={`${o.id}-${idx}`}>
                  <td>
                    <span className={`badge ${it.tipo === 'Produto' ? 'badge--neutro' : 'badge--ativo'}`}>
                      {it.tipo === 'Produto' ? 'Peça' : 'Serviço'}
                    </span>
                  </td>
                  <td>{it.descricao}</td>
                  <td className="table__muted">{it.colaboradorNome ?? '—'}</td>
                  <td className="table__numeric">{formatarQuantidade(it.quantidade)}</td>
                  <td className="table__numeric">{fmtMoeda.format(it.precoUnitario)}</td>
                  <td className="table__numeric">
                    {it.valorDesconto > 0 ? `− ${fmtMoeda.format(it.valorDesconto)}` : '—'}
                  </td>
                  <td className="table__numeric">{fmtMoeda.format(it.subtotal)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} className="table__primary">
                  Peças {fmtMoeda.format(o.totalPecas)} · Serviços {fmtMoeda.format(o.totalServicos)}
                  {o.descontoGeral > 0 ? ` · Desc. geral − ${fmtMoeda.format(o.descontoGeral)}` : ''}
                </td>
                <td className="table__numeric table__primary">{fmtMoeda.format(o.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
